/**
 * F009: Document Security Panel — Sprint 21
 *
 * Displays flagged documents with non-TFN PII and redaction status (Module 3).
 * Features:
 * - Table of flagged documents: name, PII, redaction status
 * - "Flag Document" button → opens flagging form (document selector and PII controls)
 * - "Verify Redaction" per flagged document
 * - Green "Redaction Verified" badge when verified
 */

import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Loader2, Flag, ShieldCheck, CheckCircle } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

// ============================================================================
// TYPES
// ============================================================================

interface DocumentSecurityFlag {
  id: string;
  documentId: string;
  containsPii: boolean;
  piiTypes: string[];
  redactionRequired: boolean;
  redactionVerified: boolean;
  redactionVerifiedBy: string | null;
  redactionVerifiedDate: string | null;
  flaggedBy: string | null;
  flaggedAt: string;
  notes: string | null;
}

interface DealDocument {
  id: string;
  documentName: string;
  documentType: string;
}

interface DocumentSecurityPanelProps {
  dealId: string;
}

// ============================================================================
// HELPERS
// ============================================================================

const PII_TYPE_OPTIONS = [
  "Name",
  "Address",
  "Date of Birth",
  "Phone Number",
  "Email",
  "Bank Account",
  "Passport",
  "Driver Licence",
  "Medicare",
  "Other",
];

// ============================================================================
// FLAG DOCUMENT FORM (shown in AlertDialog)
// ============================================================================

interface FlagDocumentFormProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  dealId: string;
  onSuccess: () => void;
}

function FlagDocumentForm({
  open,
  onOpenChange,
  dealId,
  onSuccess,
}: FlagDocumentFormProps) {
  const { toast } = useToast();
  const [selectedDocumentId, setSelectedDocumentId] = useState<string>("");
  const [containsPii, setContainsPii] = useState(false);
  const [piiTypes, setPiiTypes] = useState<string[]>([]);
  const [redactionRequired, setRedactionRequired] = useState(false);

  // Fetch deal documents for the selector
  const { data: documents = [] } = useQuery<DealDocument[]>({
    queryKey: [`/api/crm/deals/${dealId}/documents`],
    enabled: !!dealId && open,
  });

  const flagMutation = useMutation({
    mutationFn: async () => {
      const response = await apiRequest(
        "POST",
        `/api/compliance/documents/${selectedDocumentId}/security`,
        {
          containsPii,
          piiTypes: containsPii ? piiTypes : [],
          redactionRequired,
        }
      );
      return response.json();
    },
    onSuccess: () => {
      toast({
        title: "Document flagged",
        description: "Security record created successfully.",
      });
      // Reset form
      setSelectedDocumentId("");
      setContainsPii(false);
      setPiiTypes([]);
      setRedactionRequired(false);
      onOpenChange(false);
      onSuccess();
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message || "Failed to flag document",
        variant: "destructive",
      });
    },
  });

  const handlePiiTypeToggle = (piiType: string) => {
    setPiiTypes((prev) =>
      prev.includes(piiType)
        ? prev.filter((t) => t !== piiType)
        : [...prev, piiType]
    );
  };

  const isValid = selectedDocumentId && containsPii;

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent data-testid="flag-document-form">
        <AlertDialogHeader>
          <AlertDialogTitle>Flag Document</AlertDialogTitle>
          <AlertDialogDescription>
            Select a document and indicate what sensitive information it
            contains.
          </AlertDialogDescription>
        </AlertDialogHeader>

        <div className="space-y-4 py-2">
          {/* Document selector */}
          <div className="space-y-1">
            <Label htmlFor="doc-selector" className="text-sm font-medium">
              Document
            </Label>
            <Select
              value={selectedDocumentId}
              onValueChange={setSelectedDocumentId}
            >
              <SelectTrigger
                id="doc-selector"
                className="h-8 text-sm"
                data-testid="select-flag-document"
              >
                <SelectValue placeholder="Select a document..." />
              </SelectTrigger>
              <SelectContent>
                {documents.length === 0 ? (
                  <SelectItem value="none" disabled>
                    No documents found
                  </SelectItem>
                ) : (
                  documents.map((doc) => (
                    <SelectItem key={doc.id} value={doc.id}>
                      {doc.documentName}
                    </SelectItem>
                  ))
                )}
              </SelectContent>
            </Select>
          </div>

          {/* PII checkbox */}
          <div className="flex items-start gap-2">
            <Checkbox
              id="contains-pii"
              checked={containsPii}
              onCheckedChange={(checked) => {
                setContainsPii(!!checked);
                if (!checked) setPiiTypes([]);
              }}
              data-testid="checkbox-contains-pii"
            />
            <div className="space-y-2">
              <Label htmlFor="contains-pii" className="text-sm cursor-pointer">
                Contains PII (Personally Identifiable Information)
              </Label>
              {containsPii && (
                <div className="grid grid-cols-2 gap-1.5 pl-1">
                  {PII_TYPE_OPTIONS.map((piiType) => (
                    <div key={piiType} className="flex items-center gap-1.5">
                      <Checkbox
                        id={`pii-${piiType}`}
                        checked={piiTypes.includes(piiType)}
                        onCheckedChange={() => handlePiiTypeToggle(piiType)}
                        className="h-3 w-3"
                      />
                      <Label
                        htmlFor={`pii-${piiType}`}
                        className="text-xs cursor-pointer"
                      >
                        {piiType}
                      </Label>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Redaction required */}
          <div className="flex items-center gap-2">
            <Checkbox
              id="redaction-required"
              checked={redactionRequired}
              onCheckedChange={(checked) => setRedactionRequired(!!checked)}
              data-testid="checkbox-redaction-required"
            />
            <Label
              htmlFor="redaction-required"
              className="text-sm cursor-pointer"
            >
              Redaction Required
            </Label>
          </div>
        </div>

        <AlertDialogFooter>
          <AlertDialogCancel disabled={flagMutation.isPending}>
            Cancel
          </AlertDialogCancel>
          <AlertDialogAction
            onClick={() => flagMutation.mutate()}
            disabled={!isValid || flagMutation.isPending}
            data-testid="button-confirm-flag"
          >
            {flagMutation.isPending ? (
              <Loader2 className="h-3 w-3 animate-spin mr-1" />
            ) : null}
            Flag Document
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

// ============================================================================
// MAIN COMPONENT: DocumentSecurityPanel
// ============================================================================

export function DocumentSecurityPanel({
  dealId,
}: DocumentSecurityPanelProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [flagFormOpen, setFlagFormOpen] = useState(false);

  // Fetch flagged documents for this deal
  const { data: flaggedDocuments = [], isLoading } = useQuery<
    DocumentSecurityFlag[]
  >({
    queryKey: [`/api/compliance/deals/${dealId}/documents/security`],
    enabled: !!dealId,
  });

  // Verify redaction mutation
  const verifyRedactionMutation = useMutation({
    mutationFn: async ({
      documentId,
      flagId,
    }: {
      documentId: string;
      flagId: string;
    }) => {
      const response = await apiRequest(
        "PATCH",
        `/api/compliance/documents/${documentId}/security/${flagId}`,
        { redactionVerified: true }
      );
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [`/api/compliance/deals/${dealId}/documents/security`],
      });
      toast({
        title: "Redaction verified",
        description: "Document redaction has been verified.",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message || "Failed to verify redaction",
        variant: "destructive",
      });
    },
  });

  const handleFlagSuccess = () => {
    queryClient.invalidateQueries({
      queryKey: [`/api/compliance/deals/${dealId}/documents/security`],
    });
  };

  return (
    <>
      <Card data-testid="document-security-card">
        <CardHeader className="flex flex-row items-center justify-between py-3">
          <CardTitle className="text-base font-medium">
            Document Security
          </CardTitle>
          <Button
            size="sm"
            variant="outline"
            className="h-7 text-xs"
            onClick={() => setFlagFormOpen(true)}
            data-testid="button-flag-document"
          >
            <Flag className="h-3 w-3 mr-1" />
            Flag Document
          </Button>
        </CardHeader>

        <CardContent className="p-0">
          {isLoading ? (
            <div className="flex items-center justify-center py-6">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          ) : flaggedDocuments.length === 0 ? (
            <div className="text-center py-8">
              <ShieldCheck className="h-8 w-8 text-muted-foreground/40 mx-auto mb-2" />
              <p className="text-sm text-muted-foreground">
                No flagged documents
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                Flag documents containing PII for tracking and redaction management.
              </p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Document</TableHead>
                  <TableHead className="w-20 text-center">PII</TableHead>
                  <TableHead className="w-28">Redaction</TableHead>
                  <TableHead className="w-32">Verified</TableHead>
                  <TableHead className="text-right w-28">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {flaggedDocuments.map((flag) => (
                  <TableRow
                    key={flag.id}
                    data-testid={`flagged-doc-row-${flag.id}`}
                  >
                    {/* Document ID (would be document name in full impl) */}
                    <TableCell className="py-2 px-3 text-xs font-mono text-muted-foreground">
                      {flag.documentId.slice(0, 8)}...
                    </TableCell>

                    {/* PII */}
                    <TableCell className="py-2 px-3 text-center">
                      {flag.containsPii ? (
                        <Badge
                          variant="secondary"
                          className="text-xs bg-orange-100 text-orange-700"
                        >
                          Yes
                        </Badge>
                      ) : (
                        <span className="text-xs text-muted-foreground">No</span>
                      )}
                    </TableCell>

                    {/* Redaction Required */}
                    <TableCell className="py-2 px-3">
                      <Badge
                        variant="secondary"
                        className={`text-xs ${
                          flag.redactionRequired
                            ? "bg-yellow-100 text-yellow-700"
                            : "bg-gray-100 text-gray-600"
                        }`}
                      >
                        {flag.redactionRequired ? "Required" : "Not Required"}
                      </Badge>
                    </TableCell>

                    {/* Redaction Verified */}
                    <TableCell className="py-2 px-3">
                      {flag.containsPii && flag.redactionVerified ? (
                        <div className="flex items-center gap-1">
                          <CheckCircle className="h-3 w-3 text-green-600" />
                          <Badge
                            variant="secondary"
                            className="text-xs bg-green-100 text-green-700"
                          >
                            Verified
                          </Badge>
                        </div>
                      ) : flag.containsPii && flag.redactionRequired ? (
                        <Badge
                          variant="outline"
                          className="text-xs text-muted-foreground"
                        >
                          Pending
                        </Badge>
                      ) : (
                        <span className="text-xs text-muted-foreground">N/A</span>
                      )}
                    </TableCell>

                    {/* Actions */}
                    <TableCell className="py-2 px-3 text-right">
                      {flag.containsPii && flag.redactionRequired && !flag.redactionVerified && (
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-6 text-xs"
                          onClick={() =>
                            verifyRedactionMutation.mutate({
                              documentId: flag.documentId,
                              flagId: flag.id,
                            })
                          }
                          disabled={verifyRedactionMutation.isPending}
                          data-testid={`button-verify-redaction-${flag.id}`}
                        >
                          {verifyRedactionMutation.isPending ? (
                            <Loader2 className="h-3 w-3 animate-spin" />
                          ) : (
                            "Verify Redaction"
                          )}
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Flag Document Form Dialog */}
      <FlagDocumentForm
        open={flagFormOpen}
        onOpenChange={setFlagFormOpen}
        dealId={dealId}
        onSuccess={handleFlagSuccess}
      />
    </>
  );
}
